<script setup>
import { computed, getCurrentInstance, useAttrs } from "vue";
import { useConfirm } from "primevue/useconfirm";

const props = defineProps({
    text: String,
    desc: String,
    btn: String,
    disabled: Boolean,
    tone: {
        type: String,
        default: "danger",
        validator: (v) => ["danger", "neutral"].includes(v),
    },
    icon: { type: String, default: null },
});
const emit = defineEmits(["confirmed"]);
const attrs = useAttrs();
const confirm = useConfirm();
const instance = getCurrentInstance();

// Layout-transparent by default (display:contents) so a slotted trigger dropped
// into a dropdown/flow sits as if this wrapper were absent. display:contents
// emits no box, so a caller's layout utility (ml-auto, flex-shrink-0) is a
// no-op — switch to inline-flex when the caller passes a class so those apply.
const hasWrapperClass = computed(() => Boolean(attrs.class));
const rootClass = computed(() => (hasWrapperClass.value ? "inline-flex" : "contents"));

// The confirm dialog is service-owned and outlives this component. A kebab host
// built on a PrimeVue Popover renders its panel with v-if, so clicking the
// dialog (an outside-click that dismisses the popover) unmounts this ConfirmAction
// before accept runs. Vue's emit() can't reach a listener once the instance is
// torn down, so the bound @confirmed handler is captured here at trigger time and
// invoked directly when the component is already unmounted — otherwise emit() is
// used as normal (keeping the public @confirmed contract and emit tracking intact).
const fireConfirmed = (boundHandler) => {
    if (instance?.isUnmounted) {
        if (Array.isArray(boundHandler)) boundHandler.forEach((h) => h?.());
        else boundHandler?.();
        return;
    }
    emit("confirmed");
};

const onTrigger = () => {
    if (!props.text) {
        emit("confirmed");
        return;
    }
    const boundHandler = instance?.vnode?.props?.onConfirmed;
    confirm.require({
        group: "korgi-confirm",
        // Deliberately NOT position:"top". Top anchoring lives on the mask in
        // ConfirmActionDialog instead, because everything derived from `message`
        // dies the moment confirm.close() clears it — and that happens at the START
        // of the closing animation on a backdrop click, so the panel used to slide
        // away from the top mid-fade.
        // Custom fields read by ConfirmActionDialog's #container slot.
        tone: props.tone,
        icon: props.icon,
        text: props.text,
        desc: props.desc,
        btn: props.btn,
        disabled: props.disabled,
        accept: () => fireConfirmed(boundHandler),
    });
};
</script>

<template>
    <span :class="rootClass" @click="onTrigger">
        <slot />
    </span>
</template>
